import { useEffect, useState } from 'react';
import { api, zeitpunkt, datum } from '../api.ts';
import { Abschnittsband, kennung, useAbschnitteAusDom }
  from '../teile/Abschnittsband.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage }
  from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

// Die Befundarten aus v_auftrag_befund, ausgeschrieben. Ein Schluesselwort
// wie "sm_doppelt" sagt dem, der es nicht gebaut hat, gar nichts.
const BEFUND_TEXT: Record<string, string> = {
  sm_doppelt: 'Dieselbe SM-Nummer an mehreren Aufträgen',
  kls_doppelt: 'Dieselbe KLS-ID an mehreren Aufträgen',
  sm_fehlt: 'Keine SM-Nummer: im Telekom-Portal nicht auffindbar',
  kls_fehlt: 'Keine KLS-ID',
  auftragstyp_fehlt: 'Kein Auftragstyp: alle 3 Gewerke gelten als unsere',
  rohrverband_fremd: 'Rohrverband steht in keiner der 8 Schreibweisen',
  roehrchen_fremd: 'Röhrchen steht in keiner der 24 Schreibweisen',
  gemeinde_fehlt: 'Keine Gemeinde: es geht kein Behördenpapier hinaus',
};

/**
 * Der Kopf einer Zustandskarte: Punkt, Titel, Stand.
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „kannst du bei den 3 letzten seiten das grau in grau anpassen. das wirkt
 *    so unsortiert. dynamischer und innovativer gestalten."
 *
 * ER STEHT HIER EINMAL und nicht zehnmal in der Seite. Zehn Abschriften
 * heissen, dass die elfte Karte ihren Punkt nicht hat - dieselbe Lehre wie
 * beim Seitenrand und bei der Schriftgrösse.
 *
 * `offen` ist die Zahl, um die es geht: 0 heisst grün, alles andere färbt.
 * `hart` sagt, ob es aufhält (rot) oder nur eine Arbeit ist (orange) - eine
 * Gemeinde ohne Mailadresse hält ein Papier auf, ein leerer Schlüssel meistens
 * nicht.
 */
/* UND ER IST DAS SPRUNGZIEL DES ABSCHNITTSBANDES.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „er soll den menüpunkt als chip einfärben wenn man beim scrollen in dem
 *    bereich ist"
 *
 * Die Kennung entsteht HIER, an der einen Stelle, die jede Karte dieses
 * Bildschirms baut - eine zweite Liste „so heissen die Karten" neben dem
 * Band liefe beim ersten Nachtrag auseinander, und dann zeigte ein Chip auf
 * nichts. `data-abschnitt` ist der Text auf dem Chip; das Band liest beides
 * aus dem Bildschirm selbst. */
function Kartenkopf(
  { titel, offen, hart, stand }:
  { titel: string; offen: number; hart?: boolean; stand?: string }
) {
  return (
    <h3 id={'zu-' + kennung(titel)} data-abschnitt={titel}>
      <span className={'zustandspunkt '
        + (offen === 0 ? 'gut' : hart ? 'hart' : 'offen')} />
      {titel}
      <span className="weg">
        {stand ?? (offen === 0 ? 'nichts offen'
          : `${offen} ${offen === 1 ? 'Punkt' : 'Punkte'} offen`)}
      </span>
    </h3>
  );
}

export default function Systemzustand() {
  const [d, setD] = useState<any>(null);
  /* DIE ANBINDUNG WIRD GETRENNT GEHOLT, und ihr Ausfall hält den Rest nicht
     auf: sie hängt an einem anderen Recht (`konfiguration.pflegen`), und wer
     es nicht hat, soll den Systemzustand trotzdem sehen. Ein Bildschirm, der
     wegen eines Blocks ganz leer bleibt, sieht aus wie ein kaputter. */
  const [ab, setAb] = useState<any>(null);
  /* WELCHE KARTEN WIRKLICH DASTEHEN, LIEST DAS BAND AUS DEM BILDSCHIRM.
     Die Hälfte von ihnen erscheint nur, wenn es etwas zu melden gibt; eine
     Liste daneben wäre beim ersten Nachtrag falsch. */
  const abschnitte = useAbschnitteAusDom();
  /* ZWEI ABRUFE, EINE LADEFUNKTION. Sie stand zweimal im Effekt und liess
     sich damit von aussen nicht rufen; ein Bildschirm, der lauter Befunde
     zeigt, gehört zu denen, die nach einem Schreibvorgang nachziehen. */
  function laden() {
    api.zustand().then(setD).catch(() => {});
    api.anbindung().then(setAb).catch(() => setAb(null));
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);
  if (!d) return null;

  /* DIE ZWEI ZAHLEN, DIE „WAS OFFEN IST" WIRKLICH MEINT.
     Ein leerer Schlüssel ist meistens Absicht - LEER HEISST AUS. Offen ist
     nur, was PFLICHT ist und fehlt, und was nicht hinausgegangen ist. */
  const pflichtLeer = (d.konfiguration_luecken ?? [])
    .reduce((n: number, z: any) => n + Number(z.pflicht_leer), 0);
  const nichtGesendet = (d.nachricht_stand ?? [])
    .reduce((n: number, z: any) => n + Number(z.nicht_gesendet), 0);

  return (
    <>
      <div className="kopf">
        <h1>Systemzustand</h1>
        <span className="weg">
          Was früher 8 Diagnose-Menüpunkte waren
        </span>
      </div>
      <Abschnittsband abschnitte={abschnitte} />

      <div className="inhalt">

        {ab && <Anbindung d={ab} />}

        {/* DER UMZUGSBLOCK IST WEG (Migration 062, 28.08.2026).
            ORIGINALTEXT MARVIN
              „hebe hier alle verknüpfungen auf ... es gibt keine altlasten
               mehr!"
            Hier stand ganz oben, wie weit der Umzug ist: zehn Bereiche mit
            ihrem Besitzer, die Nachzügler von früher und die letzten zwölf
            Übernahmeläufe. Alle drei Tabellen sind gefallen.
            EIN FORTSCHRITTSBALKEN, DER FERTIG IST, IST KEINE AUSKUNFT MEHR,
            sondern eine Baustelle, die es nicht mehr gibt - und wer ihn sieht,
            sucht nach dem Rest. Was von der Herkunft übrig ist, steht weiter
            unten bei den einzelnen Tabellen: dort beantwortet es „woher kam
            dieser Wert", und das gilt weiter. */}

        {/* STIMMT DIE AUFTRAGSZAHL?
            ORIGINALTEXT MARVIN (02.09.2026)
              „Was ist mit der Auftragsdifferenz? Hast du diese behoben?"
            „499" ist keine Auskunft. Diese Karte teilt die Zahl auf und
            beantwortet die Frage jederzeit selbst - vor und nach jeder
            Übernahme, mit derselben Rechnung (v_auftragszahl). */}
        {d.auftragszahl && (
          <div className="karte">
            <Kartenkopf
              titel="Die Auftragszahl"
              offen={Number(d.auftragszahl.zusammenlegbar ?? 0)}
              hart={false}
              stand={Number(d.auftragszahl.zusammenlegbar ?? 0) === 0
                ? 'keine Doppelgänger, die zusammengehen'
                : `${d.auftragszahl.zusammenlegbar} könnten zusammengelegt werden`} />
            <div className="leib">
              <div className="kachelreihe">
                <div className="kachel">
                  <div className="wert">{d.auftragszahl.auftraege}</div>
                  <div className="was">Aufträge</div>
                </div>
                <div className="kachel">
                  <div className="wert" style={{ color: Number(d.auftragszahl.ohne_arbeit) ? 'var(--neu)' : undefined }}>
                    {d.auftragszahl.ohne_arbeit}
                  </div>
                  <div className="was">ohne jede Arbeit</div>
                </div>
                <div className="kachel">
                  <div className="wert">{d.auftragszahl.haeuser_mehrfach}</div>
                  <div className="was">Häuser mit mehreren</div>
                </div>
                <div className="kachel">
                  <div className="wert" style={{ color: Number(d.auftragszahl.zusammenlegbar) ? 'var(--neu)' : undefined }}>
                    {d.auftragszahl.zusammenlegbar}
                  </div>
                  <div className="was">gehen zusammen</div>
                </div>
                <div className="kachel">
                  <div className="wert">{d.auftragszahl.bleiben_stehen}</div>
                  <div className="was">bleiben stehen</div>
                </div>
                <div className="kachel">
                  <div className="wert">{d.auftragszahl.archiviert}</div>
                  <div className="was">archiviert</div>
                </div>
                {/* EINE KACHEL, DIE IMMER NULL ZEIGT, STEHT GAR NICHT ERST DA.
                    Sie erscheint, sobald der erste Mängelauftrag da ist - und
                    dann erklärt sie einen Teil der Häuser mit mehreren. */}
                {Number(d.auftragszahl.maengelbeseitigung ?? 0) > 0 && (
                  <div className="kachel">
                    <div className="wert">{d.auftragszahl.maengelbeseitigung}</div>
                    <div className="was">Mängelbeseitigung</div>
                  </div>
                )}
              </div>
              <p className="klein leise" style={{ margin: '10px 0 0' }}>
                {Number(d.auftragszahl.haeuser_mehrfach) > 0 ? (
                  <>
                    Auf {d.auftragszahl.haeuser_mehrfach} Häusern liegen{' '}
                    {d.auftragszahl.auftraege_darauf} Aufträge. Zusammengelegt wird
                    über die BA/FA-ID und über das Haus, und nur, wenn am
                    weichenden NICHTS hängt - kein Termin, kein Aufmaß, keine
                    Datei, kein Papier.{' '}
                    {Number(d.auftragszahl.bleiben_stehen) > 0
                      ? 'Was stehen bleibt, trägt beidseitig Arbeit oder 2 '
                        + 'verschiedene Kennungen und gehört einzeln angesehen.'
                      : 'Es bleibt nichts stehen.'}{' '}
                  </>
                ) : (
                  <>Auf keinem Haus liegen mehrere Aufträge.{' '}</>
                )}
                {d.auftragszahl.ohne_bafa} Aufträge tragen keine BA/FA-ID.
              </p>
            </div>
          </div>
        )}

        {/* ORTE, IN DENEN WIR NICHT ARBEITEN - UND WO DIE LISTE STEHT.
            ================================================================
            ORIGINALTEXT MARVIN (03.09.2026)
              „dann habe ich doch gesagt sollst du überall im system die
               alten/falschen orte ausblenden, wo führe ich die liste dazu."

            **Die zweite Frage ist die wichtigere: er hat die Liste gesucht
            und nicht gefunden.** Sie steht in den Stammdaten unter „Ort und
            Gemeinde", in einer eigenen Karte. Hier steht die Zahl mit dem
            Weg dorthin - und die Aufteilung, die erklärt, warum trotzdem
            noch etwas dasteht: **archiviert wird nur, woran nichts hängt.**
            Ein Auftrag mit Termin oder Aufmaß bleibt stehen, ist seit .87
            aber überall dort ausgeblendet, wo er nichts zu suchen hat. */}
        {d.orte_ohne_bezug && d.orte_ohne_bezug.auftraege > 0 && (
          <div className="karte">
            <Kartenkopf
              titel="Orte, in denen wir nicht arbeiten"
              offen={d.orte_ohne_bezug.mit_arbeit}
              stand={`${d.orte_ohne_bezug.orte} `
                + `${d.orte_ohne_bezug.orte === 1 ? 'Ort' : 'Orte'}, `
                + `${d.orte_ohne_bezug.auftraege} Aufträge`} />
            <div className="leib">
              <div className="kachelreihe">
                <div className="kachel gut">
                  <div className="wert">{d.orte_ohne_bezug.archiviert}</div>
                  <div className="was">archiviert, aus allen Listen heraus</div>
                </div>
                <div className={'kachel'
                  + (d.orte_ohne_bezug.mit_arbeit > 0 ? ' offen' : '')}>
                  <div className="wert">{d.orte_ohne_bezug.mit_arbeit}</div>
                  <div className="was">stehen noch, weil Arbeit daran hängt</div>
                </div>
              </div>
              <p className="klein leise" style={{ marginBottom: 0 }}>
                Die Liste wird in den <b>Stammdaten</b> unter „Ort und
                Gemeinde" geführt, in der Karte <b>„Orte, in denen wir nicht
                arbeiten"</b> ganz unten. Wer dort einen Ort einträgt, sorgt
                dafür, dass aus ihm kein Auftrag mehr angelegt wird und dass er
                aus Auswertung, Kapazität, Behördenmappe und Gemeindeliste
                verschwindet. Aufträge, an denen schon Termine, Aufmaße oder
                Papiere hängen, werden nicht archiviert - sie bleiben
                auffindbar, zählen aber nirgends mehr mit.
              </p>
            </div>
          </div>
        )}

        {/* DIE LEITUNG ZUR DATENBANK.
            ================================================================
            ORIGINALTEXT MARVIN (03.09.2026)
              „es gibt dann nämlich auch ein ladeproblem auf dem server
               komplett ... es scheint so als hängt sich was auf. irgendwas
               steigt da aus beim VPS server. einfach so."

            Er ist nicht ausgestiegen, er hat GEWARTET. Der Pool hatte zehn
            Verbindungen und keine Wartegrenze, und der Anmelder wurde vor
            JEDER Anfrage aus der Datenbank geholt - auch für jedes Bild und
            jede Kartenkachel. Beides ist behoben; **was bleibt, ist die
            Anzeige, die es damals nicht gab.**

            `wartend` ist die Zahl, auf die es ankommt: im Normalbetrieb null.
            Steht dort etwas, stehen Anfragen in der Schlange - und dann sieht
            der Bildschirm aus wie ein kaputter Server. */}
        {/* DIE ABLAGE WIRKLICH NACHSEHEN.

            ORIGINALTEXT MARVIN (07.09.2026)
              „3. was tun wir um sauber zu prüfen und 4. wie beheben wir
               diesen Datenfehler?"

            **DIESE KARTE STEHT IMMER DA, auch wenn nichts offen ist** - und
            das ist der Unterschied zu jeder anderen auf diesem Bildschirm.
            Die anderen sind BEFUNDE (eine Karte, die immer null zeigt, wird
            nach 2 Wochen nicht mehr gelesen); diese ist das WERKZEUG, mit dem
            man den Befund überhaupt erst erhebt. **Ein Weg, den man nicht
            findet, ist so gut wie keiner** - und wer prüfen will, sucht den
            Knopf genau dann, wenn gerade nichts gemeldet ist. */}
        <AblagePruefen />

        <VorbereitungNachsehen />

        {/* WELCHE NACHWEISE WIRKLICH FEHLEN - MIT NAMEN UND BAUSTELLE.

            ORIGINALTEXT MARVIN (07.09.2026)
              „Alte dateien lassen sich nicht öffnen. es kommt ein fehler,
               manche bilder sind auch nicht da obwohl alles in den ordnern
               war."

            Der Merker an der Zeile fällt seit dieser Version, sobald der
            Inhalt wieder abgelegt ODER gelesen wird - jede Datei, die er noch
            einmal hochlädt, heilt damit von selbst. Was hier stehen bleibt,
            fehlt WIRKLICH, und **eine Zahl allein schickt niemanden an eine
            Stelle, an der er etwas tun kann**: es steht die Baustelle daneben
            und der Dateiname, der noch einmal in den Ordner gehört.

            SIE ERSCHEINT NUR, WENN ETWAS DA IST. Eine Karte, die immer „0"
            zeigt, wird nach 2 Wochen nicht mehr gelesen - und dann auch an
            dem Tag nicht, an dem etwas darin steht. */}
        {!!d.dateien_ohne_inhalt?.length && (
          <div className="karte">
            <Kartenkopf
              titel="Nachweise ohne Inhalt"
              offen={d.dateien_ohne_inhalt.length}
              hart
              stand={`${d.dateien_ohne_inhalt.reduce(
                (n: number, z: any) => n + z.dateien, 0)} Datei(en) an `
                + `${d.dateien_ohne_inhalt.length} Baustelle(n)`} />
            <div className="leib">
              <p className="klein leise" style={{ marginTop: 0 }}>
                Von diesen Dateien steht die Zeile in der Akte, der Inhalt
                liegt nicht mehr in der Ablage. Wer sie noch einmal in ihren
                Ordner lädt, hat sie wieder - der Vermerk fällt dabei von
                selbst, es ist kein zweiter Handgriff nötig.
              </p>
              <div className="rollt">
                <table className="liste">
                  <thead>
                    <tr>
                      <th>Baustelle</th><th>KLS</th>
                      <th>Dateien</th><th>Weggeräumt</th><th>Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.dateien_ohne_inhalt.map((z: any) => (
                      <tr key={z.auftrag_id}>
                        <td>
                          {z.strasse} {z.hausnummer}{z.hausnummer_zusatz ?? ''}
                          <div className="neben">{z.plz} {z.ort_text}</div>
                          <div className="neben">{(z.namen ?? []).join(', ')}</div>
                        </td>
                        <td className="kennung">
                          {z.kls_id ?? <span className="leise">fehlt</span>}
                        </td>
                        <td className="einfarbig">{z.dateien}</td>
                        <td>{datum(z.seit)}</td>
                        <td>{{
                          import: 'nach dem Einlesen',
                          papierkorb: 'aus dem Papierkorb',
                          auftrag: 'mit einem gelöschten Auftrag',
                        }[z.art as string] ?? 'unbekannt'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {d.poolstand && (
          <div className="karte">
            <Kartenkopf
              titel="Die Leitung zur Datenbank"
              offen={d.poolstand.wartend}
              hart={d.poolstand.wartend > 0}
              stand={d.poolstand.wartend > 0
                ? `${d.poolstand.wartend} Anfragen warten auf eine Verbindung`
                : 'niemand wartet'} />
            <div className="leib">
              <div className="kachelreihe">
                <div className="kachel">
                  <div className="wert">{d.poolstand.offen}</div>
                  <div className="was">
                    von {d.poolstand.hoechstens} Verbindungen offen
                  </div>
                </div>
                <div className="kachel">
                  <div className="wert">{d.poolstand.frei}</div>
                  <div className="was">davon gerade frei</div>
                </div>
                <div className={'kachel '
                  + (d.poolstand.wartend > 0 ? 'hart' : 'gut')}>
                  <div className="wert">{d.poolstand.wartend}</div>
                  <div className="was">Anfragen in der Schlange</div>
                </div>
              </div>
              <p className="klein leise" style={{ marginBottom: 0 }}>
                {d.poolstand.wartend > 0
                  ? 'Es warten Anfragen auf eine freie Verbindung. Das ist der '
                    + 'Zustand, in dem der Bildschirm „wird geladen" sagt und '
                    + 'nicht zurückkommt. Meistens hält eine langsame Abfrage '
                    + 'oder ein laufender Import die Leitungen; nach 30 '
                    + 'Sekunden bricht eine Abfrage von selbst ab.'
                  : 'Jede Anfrage bekommt sofort eine Verbindung. Wer nach 8 '
                    + 'Sekunden keine bekommt, erhält einen Fehler statt einer '
                    + 'Seite, die sich nie aufbaut.'}
              </p>
            </div>
          </div>
        )}

        {/* WIE VOLL DIE LEITUNG ZU DEN KARTENDIENSTEN IST.

            ORIGINALTEXT MARVIN (03.09.2026, mit fünf Bildschirmfotos)
              „es läd sehr langsam, bzw garnicht."

            Auf Bild 1 lag das Luftbild ZUR HÄLFTE da, auf Bild 5 stand ein
            Verbindungsüberlauf zu einem Namen, der auflöst. **Ein Dienst, der
            einen Teil liefert, ist nicht ausgefallen, sondern überfahren:**
            ein Ausschnitt sind rund zwölf Kacheln, mit der Beschriftung
            vierundzwanzig, und die gingen alle gleichzeitig an denselben
            Landesdienst.

            Seit .100 gehen höchstens drei auf einmal an denselben Rechner,
            und wer dreimal nicht geantwortet hat, wird eine Minute nicht
            gefragt. Was hier steht, ist der Beweis dafür - und die Antwort
            auf den nächsten Anruf: **eine Sperre, die schweigt, ist von einem
            Ausfall nicht zu unterscheiden.** */}
        {d.kartendienste && (
          <div className="karte">
            <Kartenkopf
              titel="Die Leitung zu den Kartendiensten"
              offen={(d.kartendienste.gesperrt ?? []).length}
              stand={(d.kartendienste.gesperrt ?? []).length > 0
                ? `${d.kartendienste.gesperrt.length} Adressen werden gerade `
                  + 'in Ruhe gelassen'
                : 'kein Dienst gesperrt'} />
            <div className="leib">
              <div className="kachelreihe">
                <div className="kachel">
                  <div className="wert">{d.kartendienste.laeuft}</div>
                  <div className="was">Kacheln gerade unterwegs</div>
                </div>
                <div className="kachel">
                  <div className="wert">{d.kartendienste.wartet}</div>
                  <div className="was">warten in der Schlange</div>
                </div>
                <div className={'kachel '
                  + ((d.kartendienste.gesperrt ?? []).length > 0 ? 'offen' : 'gut')}>
                  <div className="wert">
                    {(d.kartendienste.gesperrt ?? []).length}
                  </div>
                  <div className="was">Adressen gesperrt</div>
                </div>
              </div>
              {(d.kartendienste.gesperrt ?? []).length > 0 && (
                <table>
                  <thead>
                    <tr><th>Adresse</th><th>wird wieder gefragt in</th></tr>
                  </thead>
                  <tbody>
                    {d.kartendienste.gesperrt.map((g: any, i: number) => (
                      <tr key={i}>
                        <td className="klein" style={{ wordBreak: 'break-all' }}>
                          {g.dienst}
                        </td>
                        <td className="einfarbig">{g.sekunden} s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="klein leise" style={{ marginBottom: 0 }}>
                {(d.kartendienste.gesperrt ?? []).length > 0
                  ? 'Diese Adressen haben mehrfach hintereinander nicht '
                    + 'geantwortet und werden eine Minute lang nicht noch '
                    + 'einmal gefragt. Das ist keine Abschaltung: die Sperre '
                    + 'läuft von selbst ab, und die erste erfolgreiche Antwort '
                    + 'räumt sie ab. „Dienste prüfen" fragt trotzdem.'
                  : 'Höchstens 3 Kacheln gehen gleichzeitig an denselben '
                    + 'Rechner. Ein Landesdienst rechnet jedes Bild einzeln; '
                    + '12 Anfragen auf einmal beantwortet er nicht mehr.'}
              </p>
            </div>
          </div>
        )}

        <div className="karte">
          <Kartenkopf
            titel="Was an den Auftragsdaten nicht stimmt"
            offen={(d.datenbefund ?? []).length}
            hart={(d.datenbefund ?? []).some((b: any) => b.schwere === 'hart')}
            stand={(d.datenbefund ?? []).length === 0 ? 'nichts offen'
              : `${(d.datenbefund ?? [])
                   .reduce((n: number, b: any) => n + Number(b.n), 0)} `
                + `Aufträge in ${d.datenbefund.length} Befunden`} />
          <div className="leib">
            {(d.datenbefund ?? []).length === 0 ? (
              <p className="klein leise" style={{ margin: 0 }}>
                Keine doppelten Kennungen, keine fehlenden Kennungen, keine
                Werte neben den Listen.
              </p>
            ) : (
              <table className="liste">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Schwere</th>
                    <th>Befund</th>
                    <th style={{ width: 70 }} className="zahl">Anzahl</th>
                    <th style={{ width: 230 }}>Beispiel</th>
                  </tr>
                </thead>
                <tbody>
                  {d.datenbefund.map((b: any) => (
                    <tr key={b.befund} style={{ cursor: 'default' }}>
                      <td>
                        <span className={'gewerk ' + (b.schwere === 'hart' ? '' : 'fremd')}>
                          {b.schwere === 'hart' ? 'hält auf' : 'weich'}
                        </span>
                      </td>
                      <td className="klein">{BEFUND_TEXT[b.befund] ?? b.befund}</td>
                      <td className="zahl">{b.n}</td>
                      <td className="klein leise">{b.beispiel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* EIN STATUSWORT IST KEINE ANTWORT.
            Hier standen bis zum 25.08.2026 nur die NAMEN, und die Liste kam
            aus dem Haken `gemeinde.gepflegt` statt aus der Rechnung. Sie war
            damit zu kurz (der Haken war gesetzt, die Vorlage fehlte) und sagte
            ausserdem nicht, was zu tun ist. Beides kommt jetzt aus
            `v_gemeinde_luecken`, sortiert nach der Zahl der Adressen: dort
            kostet die Lücke am schnellsten einen Termin. */}
        {d.gemeinden_luecken.length > 0 && (
          <div className="karte">
            <Kartenkopf titel="Gemeinden ohne Pflege"
                        offen={d.gemeinden_luecken.length} hart
                        stand={`${d.gemeinden_luecken.length} von `
                          + `${d.gemeinden_luecken.length} unvollständig`} />
            <div className="leib">
              <p className="klein" style={{ marginTop: 0 }}>
                Was in welcher Gemeinde fehlt, die mit den meisten Adressen
                zuerst. Ohne Versandweg geht die Aufgrabungsanzeige
                nirgendwohin.
              </p>
              <table className="liste">
                <thead>
                  <tr>
                    <th>Gemeinde</th>
                    <th>Was fehlt</th>
                    <th className="zahl" style={{ width: 100 }}>Adressen</th>
                  </tr>
                </thead>
                <tbody>
                  {d.gemeinden_luecken.map((g: any) => (
                    <tr key={g.name} style={{ cursor: 'default' }}>
                      <td>{g.name}</td>
                      <td className="klein">
                        {[
                          g.ohne_versandweg && 'kein Empfänger',
                          g.portal_ohne_adresse && 'Portal ohne Adresse',
                          /* HIER STANDEN ZWEI FÄLLE, und der eine hat mit
                             Migration 069 seinen Gegenstand verloren:
                             „Vordruck nur benannt" hiess, früher steht ein
                             Dateiname und in der alten Ablage liegt die Datei. Es gibt
                             beides nicht mehr. Bleibt die Aufgabe, die zu
                             tun ist. */
                          g.ohne_vao_vorlage && 'kein Vordruck',
                        ].filter(Boolean).join(', ')}
                      </td>
                      <td className="zahl">{g.adressen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* WAS AN DEN EINSTELLUNGEN UND AN DER POST OFFEN IST.
            Diese Karte hiess „Woher die Werte stammen" und teilte jede Zeile
            in „von früher" und „hier angelegt". Die Spalte dahinter
            (`aus_dem_sheet`) ist mit Migration 069 gefallen: sie hiess in
            diesem Haus operativ „die Übernahme darf diese Zeile abräumen",
            und es gibt keine Übernahme.
            WAS BLEIBT, IST DIE FRAGE, DIE ETWAS AUFHÄLT: welcher Bereich hat
            Schlüssel ohne Wert, und welcher Kanal hat Post, die nicht
            hinausging. Die Herkunft einer Zeile beantwortete nie eine Frage,
            die jemand hatte; diese beiden tun es. */}
        {/* WER AUF SEINE FREISCHALTUNG WARTET (Migration 130).
            ------------------------------------------------------------------
            ORIGINALTEXT MARVIN (03.09.2026)
              „ich will gerne, dass sich die Auftragnehmer regisrieren, aber
               sie noch nichts aktiv sehen oder bedienen können."

            EIN WARTENDER, DEN NIEMAND SIEHT, WARTET FÜR IMMER. Er kommt
            herein, richtet sich ein, sieht eine Willkommensseite - und wenn
            niemand den Haken wegnimmt, bleibt es dabei. Die Karte steht
            deshalb dort, wo der Innendienst ohnehin nachsieht, und sie ist
            ORANGE und nicht rot: es hält nichts auf, es wartet jemand. */}
        <div className="karte">
          <Kartenkopf
            titel="Zugänge, die auf Freischaltung warten"
            offen={(d.wartende ?? []).length}
            stand={(d.wartende ?? []).length === 0
              ? 'niemand wartet'
              : `${(d.wartende ?? []).length} warten`} />
          <div className="leib">
            {(d.wartende ?? []).length === 0 ? (
              <p className="klein leise" style={{ margin: 0 }}>
                Niemand wartet. Der Haken steht unter „Benutzer und Rechte" an
                jedem Zugang.
              </p>
            ) : (
              <>
                <table className="liste">
                  <thead>
                    <tr>
                      <th>Name</th><th>Firma</th>
                      <th style={{ width: 130 }}>wartet seit</th>
                      <th style={{ width: 120 }}>Passwort</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.wartende ?? []).map((z: any) => (
                      <tr key={z.id} style={{ cursor: 'default' }}>
                        <td>
                          {z.name}
                          <div className="klein leise">{z.email}</div>
                        </td>
                        <td>{z.firma ?? <span className="leise">Innendienst</span>}</td>
                        <td>{datum(z.wartet_seit)}</td>
                        <td className="klein">
                          {z.hat_passwort
                            ? 'gesetzt'
                            : <span className="leise">noch keins</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="klein leise">
                  Sie kommen herein und sehen nur die Warteseite. Freischalten
                  geht unter „Benutzer und Rechte"; dabei geht eine Mail an sie
                  hinaus.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="karte">
          <Kartenkopf
            titel="Was offen ist"
            offen={pflichtLeer + nichtGesendet}
            hart={pflichtLeer > 0}
            stand={pflichtLeer + nichtGesendet === 0
              ? 'nichts offen'
              : [pflichtLeer && `${pflichtLeer} Pflichtschlüssel leer`,
                 nichtGesendet && `${nichtGesendet} nicht gesendet`]
                .filter(Boolean).join(', ')} />
          <div className="leib">
            <table className="liste">
              <thead>
                <tr>
                  <th>Konfiguration</th>
                  <th className="zahl" style={{ width: 100 }}>Schlüssel</th>
                  <th className="zahl" style={{ width: 100 }}>ohne Wert</th>
                  <th className="zahl" style={{ width: 130 }}>davon Pflicht</th>
                </tr>
              </thead>
              <tbody>
                {(d.konfiguration_luecken ?? []).map((z: any) => (
                  <tr key={z.bereich} style={{ cursor: 'default' }}>
                    <td>{z.bereich}</td>
                    <td className="zahl">{z.schluessel}</td>
                    <td className="zahl">{z.ohne_wert}</td>
                    <td className="zahl">
                      {Number(z.pflicht_leer) > 0
                        ? <b className="rot">{z.pflicht_leer}</b>
                        : z.pflicht_leer}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="klein leise">
              Ein leerer Schlüssel ist meistens Absicht - LEER HEISST AUS. Nur
              die letzte Spalte hält etwas auf.
            </p>
            <table className="liste" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Nachrichten</th>
                  <th className="zahl" style={{ width: 100 }}>Zeilen</th>
                  <th className="zahl" style={{ width: 120 }}>nicht gesendet</th>
                  <th style={{ width: 130 }}>älteste</th>
                </tr>
              </thead>
              <tbody>
                {(d.nachricht_stand ?? []).map((z: any) => (
                  <tr key={z.kanal} style={{ cursor: 'default' }}>
                    <td>{z.kanal}</td>
                    <td className="zahl">{z.zeilen}</td>
                    <td className="zahl">
                      {Number(z.nicht_gesendet) > 0
                        ? <b className="rot">{z.nicht_gesendet}</b>
                        : z.nicht_gesendet}
                    </td>
                    <td className="klein leise">{datum(z.aelteste)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Eine nicht gesendete Zeile von gestern ist Arbeit. Eine von vor
              3 Monaten ist ein Fehler, den niemand gesehen hat - deshalb
              steht das Datum der ältesten daneben.
            </p>
          </div>
        </div>

        {/* WAS WIRKLICH GELAUFEN IST.
            Oben prüft der Kopf die Migrationen an ihren SPUREN, hier steht,
            was `migration_lauf` behauptet. Weichen beide ab, ist eine
            Migration halb durchgelaufen oder von Hand nachgeholt worden. */}
        <div className="karte">
          <Kartenkopf titel="Eingespielte Migrationen" offen={0}
                      stand={`${d.migrationen.length} gelaufen`} />
          <div className="leib">
            <table className="liste">
              <thead>
                <tr><th>Datei</th><th style={{ width: 130 }}>Gelaufen</th></tr>
              </thead>
              <tbody>
                {d.migrationen.map((m: any) => (
                  <tr key={m.datei} style={{ cursor: 'default' }}>
                    <td className="klein">
                      {m.datei}
                      {m.erkannt_an && (
                        <div className="leise">erkannt an {m.erkannt_an}</div>
                      )}
                    </td>
                    <td className="klein leise">{datum(m.gelaufen_am)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Jede Migration trägt sich am Ende selbst hier ein.
              Was im Ordner <code>db/migrationen</code> liegt und hier fehlt,
              ist noch nicht gelaufen.
            </p>
          </div>
        </div>

        <div className="karte">
          <Kartenkopf titel="Fristen mit ungeprüfter Herkunft"
                      offen={Number(d.fristen_ungeprueft.n)}
                      stand={Number(d.fristen_ungeprueft.n) === 0
                        ? 'keine offen'
                        : `${d.fristen_ungeprueft.n} aus dem Bestand`} />
          <div className="leib">
            <div className="wert" style={{ fontSize: 24, fontWeight: 650 }}>
              {d.fristen_ungeprueft.n}
            </div>
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Aus dem Bestand übernommen. Ob die Frist aus der Anordnung der
              Behörde oder aus unserem eigenen Antrag stammt, lässt sich nur
              durch einen Blick in den jeweiligen Ordner klären.
            </p>
          </div>
        </div>

        {/* DIE KARTE „BEFUNDE DER ÜBERNAHME" IST MIT MIGRATION 068 GEFALLEN.
            Sie zeigte, was beim Einlesen von früher verworfen oder
            umgedeutet wurde - und ohne Quelle lässt sich weder nachsehen noch
            etwas daran tun. Migration 062 hatte den Rest des Umzugs abgeräumt
            und diese eine Tabelle übersehen; gefunden hat es Prüfstand 001,
            Abschnitt 6a. Was an den Aufträgen SELBST nicht stimmt, steht
            weiter oben unter „Datenbefunde" und bleibt. */}
      </div>
    </>
  );
}


/**
 * Wie viele Dateien liegen im System, und auf welchem Weg kamen sie herein?
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „ich habe alle ordner mit allen dateien hochgeladen. nichts wird
 *    angezeigt."
 *
 * DIE FRAGE HAT ZWEI TEILE, und der Auftragsbildschirm beantwortet nur den
 * einen: „an DIESEM Auftrag liegt nichts". Ob das ganze System leer ist,
 * stand nirgends. Eine Null neben „aus dem Umzug" heisst: der Umzug ist nicht
 * gelaufen, ganz gleich, was jemand geklickt hat - und das ist eine andere
 * Auskunft als „dieser Auftrag hat noch keine Bilder".
 */
const WEG_TEXT: Record<string, string> = {
  upload: 'von Hand hochgeladen',
  drive: 'aus dem Umzug (ganze Ordner)',
  erzeugt: 'vom System erzeugt',
  mail: 'aus einer Mail',
};

function Dateiherkunft({ zeilen }: { zeilen: any[] }) {
  const summe = zeilen.reduce((n, z) => n + Number(z.dateien ?? 0), 0);
  const mb = (b: unknown) => {
    const n = Number(b ?? 0);
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`.replace('.', ',');
    return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`.replace('.', ',');
  };
  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <Kartenkopf titel="Dateien im System" offen={summe === 0 ? 1 : 0} hart
                  stand={summe === 0 ? 'keine einzige Datei'
                    : `${summe} Dateien`} />
      <div className="leib">
        {summe === 0 ? (
          <p className="klein" style={{ margin: 0 }}>
            <b className="rot">Es liegt keine einzige Datei im System.</b>{' '}
            Dateien kommen über den Posteingang und über den Dateireiter im
            Auftrag herein.
          </p>
        ) : (
          <>
            <table className="liste">
              <tbody>
                {zeilen.map((z: any) => (
                  <tr key={z.quelle} style={{ cursor: 'default' }}>
                    <td>{WEG_TEXT[z.quelle] ?? z.quelle}</td>
                    <td className="zahl">{z.dateien}</td>
                    <td className="klein leise">
                      an {z.auftraege} {Number(z.auftraege) === 1
                        ? 'Auftrag' : 'Aufträgen'} &middot; {mb(z.bytes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!zeilen.some((z: any) => z.quelle === 'drive') && (
              <p className="klein leise">
                Aus dem Umzug ist nichts dabei. Er ist gelaufen und
                abgeschaltet (Version .43); was jetzt hereinkommt, kommt über
                den Posteingang oder den Dateireiter
                und liest das Verzeichnis auf dem Server.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}


/**
 * Sind die Kundendaten mitgekommen?
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „haben wir die kundendaten alle syncronisiert die ich hochgeladen habe?"
 *
 * DIE KUNDENLISTE TRÄGT SIE. Kundenname, Telefon, Mobil und Mailadresse
 * kommen aus der Kundenliste der Telekom; was hier fehlt, hat in keiner Datei
 * gestanden. Bis zum 31.08.2026 stand hier „die Übernahme trägt sie" und
 * daneben ein Verweis auf den Befundbericht - beides ist mit den Migrationen
 * 062 und 068 abgeschafft, und ein Hinweis auf einen Bericht, den es nicht
 * gibt, schickt jemanden auf einen Weg ins Leere.
 *
 * WARUM DAS TROTZDEM AUF DEN BILDSCHIRM GEHÖRT: eine leere Spalte sieht in
 * der Akte genauso aus wie ein Kunde, der nichts hinterlassen hat. Der
 * Unterschied lässt sich nur an der SUMME sehen - und die stand bis zum
 * 26.08.2026 nirgends.
 */
function Kundendaten({ zahlen }: { zahlen: any }) {
  const alle = Number(zahlen.auftraege ?? 0);
  if (!alle) return null;
  const n = (x: unknown) => Number(x ?? 0);
  const zeilen: [string, number][] = [
    ['mit Kundenname', n(zahlen.kunde_name)],
    ['mit Telefon oder Mobil', n(zahlen.kunde_telefon)],
    ['mit Mailadresse', n(zahlen.kunde_email)],
  ];
  // ROT NUR IM EINEN FALL, DER WIRKLICH EINER IST. Steht überall eine Null,
  // ist der Master ohne diese Spalten eingelesen worden.
  const leer = zeilen.every(([, w]) => w === 0);
  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <Kartenkopf titel="Kundendaten" offen={leer ? 1 : 0} hart
                  stand={leer ? 'keine Kundendaten'
                    : `${n(zahlen.kunde_name)} mit Namen`} />
      <div className="leib">
        <table className="liste">
          <tbody>
            {zeilen.map(([was, wert]) => (
              <tr key={was} style={{ cursor: 'default' }}>
                <td>{was}</td>
                <td className="zahl">
                  {wert === 0 && leer ? <b className="rot">0</b> : wert}
                </td>
                <td className="klein leise">
                  von {alle} {alle === 1 ? 'Auftrag' : 'Aufträgen'}
                  {alle > 0 && ` (${Math.round((wert / alle) * 100)} %)`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="klein leise">
          {leer
            ? 'Kein einziger Auftrag trägt Kundendaten. Dann ist noch keine '
              + 'Kundenliste eingelesen worden.'
            : 'Kundenname, Telefon, Mobil und Mailadresse kommen aus der '
              + 'Kundenliste. Wo dort nichts stand, steht auch hier '
              + 'nichts - bei einem Neubau ohne Bewohner ist das richtig so. '
              + 'Nachtragen lässt es sich in der Auftragsakte, Block „Kunde".'}
        </p>
      </div>
    </div>
  );
}


/**
 * Wo fehlt die Bauakte, und welche gibt nichts her?
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „meldet er wenn ich zb einen neuen auftrag angelegt habe, aber die bauakte
 *    fehlt? es gibt nur noch einzelfälle, wo die bauakte nicht das neue format
 *    hat. wie gehen wir aber auch mit diesen um?"
 *
 * ZWEI SORTEN FALL, ZWEI HÄNDE, DESHALB ZWEI ABSCHNITTE. „fehlt" heisst:
 * besorgen. „stumm" heisst: sie liegt da, wurde gelesen und hat nichts
 * hergegeben - ein reiner Scan ohne Textebene oder ein Format, das die Muster
 * nicht treffen. Dort hilft nur Abtippen, und der Weg dorthin steht daneben.
 *
 * Die Karte verschwindet, wenn nichts offen ist. Eine Liste, die immer
 * dasteht, wird auch an dem Tag nicht gelesen, an dem etwas darin steht.
 */
function Bauakten({ zeilen }: { zeilen: any[] }) {
  if (!zeilen.length) return null;
  const fehlt = zeilen.filter((z) => z.stand === 'fehlt');
  const stumm = zeilen.filter((z) => z.stand === 'stumm');
  return (
    <div className="karte" style={{ marginTop: 14 }}>
      <Kartenkopf titel="Bauakten"
                  offen={fehlt.length + stumm.length}
                  stand={`${zeilen.length} Aufträge`
                    + (fehlt.length ? `, ${fehlt.length} ohne Akte` : '')
                    + (stumm.length ? `, ${stumm.length} stumm` : '')} />
      <div className="leib">
        {fehlt.length > 0 && (
          <>
            <h4 style={{ marginBottom: 4 }}>
              Ohne Bauakte ({fehlt.length})
            </h4>
            <p className="klein leise" style={{ marginTop: 0 }}>
              Die Bauakte ist nie angekommen. Ohne sie fehlen NVt-Standort,
              Rohrverband, Röhrchen und Breite - das fällt sonst erst auf,
              wenn jemand am Vorabend des Termins die Akte öffnet. Der Lauf
              „Aufträge ohne Bauakte" mahnt ab der Frist in
              <code> bauakte.frist_werktage</code>.
            </p>
            <table className="liste">
              <thead>
                <tr><th>Anschrift</th>
                    <th style={{ width: 120 }}>KLS-ID</th>
                    <th className="zahl" style={{ width: 130 }}>Werktage</th></tr>
              </thead>
              <tbody>
                {fehlt.slice(0, 30).map((z) => (
                  <tr key={z.auftrag_id} style={{ cursor: 'default' }}>
                    <td>{z.anschrift}<div className="klein leise">{z.ort}</div></td>
                    <td className="klein">{z.kls_id ?? ''}</td>
                    <td className="zahl">{z.werktage_alt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fehlt.length > 30 && (
              <p className="klein leise">… und {fehlt.length - 30} weitere.</p>
            )}
          </>
        )}

        {stumm.length > 0 && (
          <>
            <h4 style={{ marginBottom: 4, marginTop: fehlt.length ? 16 : 0 }}>
              Bauakten, die nichts hergeben ({stumm.length})
            </h4>
            <p className="klein leise" style={{ marginTop: 0 }}>
              Sie liegen im Auftragsordner, wurden gelesen und haben kein
              einziges Feld ergeben. Hier hilft nur Abtippen: Auftrag öffnen,
              „Bearbeiten", die Werte aus dem Deckblatt eintragen. Kommt später
              eine lesbare Fassung herein, verschwindet die Zeile von selbst.
            </p>
            <table className="liste">
              <thead>
                <tr><th>Anschrift</th>
                    <th style={{ width: 220 }}>Datei</th>
                    <th>woran es lag</th></tr>
              </thead>
              <tbody>
                {stumm.slice(0, 30).map((z) => (
                  <tr key={z.auftrag_id} style={{ cursor: 'default' }}>
                    <td>
                      {z.anschrift}
                      <div className="klein leise">
                        {z.ort}{z.kls_id ? `, KLS ${z.kls_id}` : ''}
                      </div>
                    </td>
                    <td className="klein leise">{z.bauakte_datei ?? ''}</td>
                    <td className="klein">{z.bauakte_stumm_grund ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stumm.length > 30 && (
              <p className="klein leise">… und {stumm.length - 30} weitere.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}


/**
 * WAS NOCH FEHLT, DAMIT n8n UND TELEGRAM TRAGEN.
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „wie müssen jetzt sauber N8N und telegram anbinden. was ist zu tun?"
 *
 * **DIE ANTWORT GEHÖRT INS SYSTEM UND NICHT IN EINE ANLEITUNG.** Eine
 * Anleitung sagt, was am Tag ihres Schreibens zu tun war; sie weiss nicht, was
 * hier schon steht, und beim dritten Punkt fragt man sich, ob man ihn nicht
 * letzte Woche schon erledigt hat. Diese Liste sieht nach und sagt je Punkt:
 * steht, oder fehlt - und was dann zu tun ist.
 *
 * SIE STEHT GANZ OBEN, SOLANGE ETWAS FEHLT, und klappt zusammen, sobald alles
 * steht. Ein Block, der dauerhaft „alles in Ordnung" meldet, wird nach zwei
 * Wochen nicht mehr gelesen - und dann auch an dem Tag nicht, an dem etwas
 * darin steht.
 */
function Anbindung({ d }: { d: any }) {
  /**
   * DIE AUFSCHRIFTEN, UND KEINE ZWEITE LISTE DANEBEN.
   *
   * Hier stand bis zum 09.09.2026 eine feste Liste der Gruppen - und sie war
   * schon eine zu kurz: `schnittstelle` kommt vom Server seit .131 mit und
   * stand auf keinem Bildschirm. Damit meldete diese Karte „alles
   * eingerichtet", während ein Punkt der Antwort offen war.
   *
   * **Eine halbierte Liste sieht aus wie eine ganze**, und was fehlt, fällt
   * nur dem auf, der es sucht. Gezeigt wird deshalb, WAS DER SERVER LIEFERT:
   * die bekannten Gruppen in ihrer Reihenfolge, alles Weitere dahinter mit
   * seinem Namen als Aufschrift. Dieselbe Bauart wie in
   * `regeln/umstellanleitung.ts`, die aus derselben Antwort das Blatt baut.
   */
  const TITEL: Record<string, string> = {
    mail_hinaus: 'Mail hinaus',
    mail_herein: 'Mail herein (Posteingang)',
    telegram: 'Telegram',
    telegram_rueckkanal: 'Telegram: der Rückweg (Knöpfe)',
    zweiter_faktor: 'Zweiter Faktor (2FA)',
    schnittstelle: 'Schnittstelle nach draußen',
    riegel: 'Die Riegel',
  };
  const bekannt = Object.keys(TITEL);
  const gruppen: [string, string][] = [
    ...bekannt.filter((k) => Array.isArray(d[k]) && d[k].length),
    ...Object.keys(d).filter(
      (k) => k !== 'basis' && !bekannt.includes(k)
             && Array.isArray(d[k]) && d[k].length),
  ].map((k) => [k, TITEL[k] ?? k.replace(/_/g, ' ')] as [string, string]);

  /* DIE BEIDEN LEHREN DER ALTEN LISTE STEHEN HIER WEITER, denn sie sind der
     Grund für die Reihenfolge in `TITEL`:

     DER RÜCKWEG kam vom Server seit .97 und stand bis .112 auf keinem
     Bildschirm - eine Gruppe, die kein Bildschirm nennt, ist von einer nicht
     gebauten nicht zu unterscheiden.

     DER ZWEITE FAKTOR steht in dieser Liste, WEIL er einzurichten ist. Am
     03.09.2026 hat das Einrichten mit „Im Server ist etwas schiefgegangen"
     abgebrochen, weil MDS_SCHLUESSEL in der .env fehlte; gemerkt hat es erst,
     wer es versucht hat. Eine fehlende Einrichtung gehört in die Liste der
     Dinge, die einzurichten sind. */

  const alle = gruppen.flatMap(([k]) => (d[k] ?? []) as any[]);
  const offen = alle.filter((x) => !x.steht && x.zutun);
  const [auf, setAuf] = useState(offen.length > 0);
  const [proben, setProben] = useState<Record<string, any>>({});
  const [laeuft, setLaeuft] = useState<string | null>(null);

  /**
   * WELCHE GRUPPE SICH WIRKLICH AUSPROBIEREN LÄSST.
   *
   * ORIGINALTEXT MARVIN (05.09.2026)
   *   „erstelle dazu eine saubere checkliste, mit punkt für punkt die wir dann
   *    durchgehen und vor allem testen, dass alles so klappt wie es soll!"
   *
   * **„UND VOR ALLEM TESTEN" IST DER TEIL, DER GEFEHLT HAT.** Die Liste sagt
   * „steht" oder „offen" - und ein gesetztes Token ist keine angekommene
   * Nachricht. Genau daran ist dieses Haus zweimal vorbeigelaufen: der
   * n8n-Mailablauf antwortet mit 200, auch wenn er die Mail verwirft, und die
   * Postfächer melden an eine Adresse, die es nicht mehr gibt. Beides steht
   * in der Konfiguration richtig da.
   *
   * DIE PROBE STEHT NEBEN IHRER GRUPPE und nicht als ein Knopf über allem:
   * „es geht etwas nicht" ist keine Auskunft, „Telegram nimmt sie nicht an"
   * schon.
   */
  const PROBEN: Record<string, string> = {
    mail_hinaus: 'Probemail senden',
    mail_herein: 'Nachsehen, ob etwas ankam',
    telegram: 'Probenachricht senden',
    telegram_rueckkanal: 'Webhook bei Telegram nachsehen',
  };

  async function probe(k: string) {
    setLaeuft(k);
    try {
      const r = await api.anbindungPruefen(k);
      setProben((p) => ({ ...p, [k]: r }));
    } catch (f: any) {
      setProben((p) => ({ ...p, [k]: { ok: false,
        klartext: f?.daten?.fehler ?? f?.message ?? String(f) } }));
    } finally { setLaeuft(null); }
  }

  return (
    <div className="karte">
      <Kartenkopf titel="Was noch einzurichten ist" offen={offen.length}
                  stand={offen.length === 0 ? 'alles eingerichtet'
                    : `${offen.length} ${offen.length === 1 ? 'Punkt' : 'Punkte'} offen`} />
      <div className="leib">
        {/* DIE ADRESSE, DIE n8n ANRUFEN MUSS, STEHT GANZ OBEN. Sie stand
            bisher in keiner Zeile dieses Systems - wer den Ablauf drüben
            baut, musste sie sich zusammensetzen, und ein falscher Pfad meldet
            sich mit einem 404, den niemand sieht. */}
        <p className="klein" style={{ marginTop: 0 }}>
          Diese Anlage erreicht n8n unter <code>{d.basis}</code>.
        </p>

        {/* DIESELBE LISTE ALS BLATT ZUM MITNEHMEN.
            ================================================================
            ORIGINALTEXT MARVIN (09.09.2026)
              „Dann fehlt mir immernoch die Anleitung zur Umstellung."

            Es gab sie: `docs/checkliste_umschalten.md`, 9 Punkte, seit dem
            05.09.2026 in der Lieferung - also als Datei auf dem SERVER, an
            einer Stelle, an die der Innendienst nicht kommt. **Ein Weg, den
            man nicht findet, ist so gut wie keiner.**

            SIE STEHT HIER UND NICHT IN EINEM MENÜPUNKT: das ist der
            Bildschirm, auf dem die Frage entsteht. Und sie ist DIESELBE
            Rechnung wie die Liste darunter - ein Papier, das etwas anderes
            sagt als der Bildschirm daneben, wird geglaubt und nicht der
            Bildschirm. */}
        <p style={{ marginTop: 8 }}>
          <a className="knopf" href="/api/anbindung/anleitung.pdf">
            Anleitung als PDF
          </a>{' '}
          <span className="neben">
            Dieselben Punkte zum Ausdrucken, mit dem Stand von jetzt. Kein
            Token und kein Passwort steht darauf.
          </span>
        </p>

        {!auf ? (
          <button className="knopf" onClick={() => setAuf(true)}>
            Alle Punkte anzeigen
          </button>
        ) : (
          gruppen.map(([k, titel]) => (
            <div key={k} style={{ marginTop: 12 }}>
              <b>{titel}</b>
              <table className="tabelle">
                <tbody>
                  {(d[k] ?? []).map((x: any, i: number) => (
                    <tr key={i}>
                      <td style={{ width: 90 }}>
                        <span className={'merker ' + (x.steht ? 'gruen' : '')}>
                          {x.steht ? 'steht' : 'offen'}
                        </span>
                      </td>
                      <td>
                        <b>{x.titel}</b>
                        <div className="neben">{x.was}</div>
                        {/* WAS ZU TUN IST, STEHT NUR DA, WENN ETWAS ZU TUN
                            IST. Ein Satz, der auch neben einem erledigten
                            Punkt steht, macht die Liste doppelt so lang und
                            halb so brauchbar. */}
                        {/* UND EIN BEFEHL BRAUCHT SEINE ZEILEN. Steht dort
                            etwas zum Abtippen, hängt es ohne `pre-line` in
                            einer einzigen Wurst - und wer drei Befehle in
                            einer Zeile liest, tippt den zweiten falsch ab. */}
                        {x.zutun && (
                          <div className="hinweis warnung"
                               style={{ marginTop: 6, whiteSpace: 'pre-line' }}>
                            {x.zutun}
                          </div>
                        )}
                        {x.schluessel && (
                          <div className="klein leise">
                            Konfiguration, Schlüssel <code>{x.schluessel}</code>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* UND DER KNOPF, DER ES WIRKLICH VERSUCHT. Er steht unter
                  SEINER Gruppe: „es geht etwas nicht" ist keine Auskunft.
                  Beide sendenden Proben gehen durch dieselben Riegel wie jede
                  andere Nachricht - steht der Testmodus, geht nichts hinaus,
                  und die Antwort sagt genau das. */}
              {PROBEN[k] && (
                <div style={{ marginTop: 6 }}>
                  <button className="knopf" disabled={!!laeuft}
                          onClick={() => probe(k)}>
                    {laeuft === k ? 'Einen Moment' : PROBEN[k]}
                  </button>
                  {proben[k] && (
                    <div className={'hinweis ' + (proben[k].ok ? 'erledigt' : 'warnung')}
                         style={{ marginTop: 6 }}>
                      <b>{proben[k].ok ? 'Ausprobiert' : 'Noch nicht'}</b>
                      {proben[k].klartext}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * DIE ABLAGE NACHSEHEN - Datei für Datei, auf der Platte.
 *
 * ORIGINALTEXT MARVIN (07.09.2026)
 *   „also 1. habe ich die Reihenfolge eingehalten, zweitens sind es nicht nur
 *    behördendokumente sondern auch Bilder! 3. was tun wir um sauber zu
 *    prüfen und 4. wie beheben wir diesen Datenfehler?"
 *
 * SEINE PUNKTE 1 UND 2 HABEN DEN BEFUND BERICHTIGT. Die Zuordnung
 * Papier↔Datei hängt an der Reihenfolge und betrifft nur Behördenpapiere;
 * ein Bild hängt an keinem Papier. **Was beide gemeinsam haben, ist die
 * ABLAGE** - und die lässt sich nicht in SQL fragen, sondern nur auf der
 * Platte.
 *
 * ZWEI STUFEN, UND DIE ZWEITE KOSTET. Das steht am Knopf und nicht in einer
 * Fußnote: wer eine Viertelstunde wartet, will vorher wissen, warum.
 */
function AblagePruefen() {
  const [stand, setStand] = useState<any>(null);
  const [befund, setBefund] = useState<any>(null);
  const [laeuft, setLaeuft] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [genau, setGenau] = useState(false);

  useEffect(() => {
    api.ablage().then(setStand).catch(() => {});
  }, []);

  async function lauf(anwenden: boolean) {
    setLaeuft(anwenden ? 'anwenden' : 'sehen'); setFehler(null);
    try {
      const e = await api.ablagePruefen(anwenden, genau);
      setBefund(e);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(''); }
  }

  /* WAS ETWAS ZU TUN GIBT. `da` ist der Normalfall und `weg_mit_grund` eine
     Entscheidung, die jemand getroffen hat - wer sie mitzählte, meldete den
     Normalfall als Mangel. */
  const sorgen = (befund?.lagen ?? []).filter(
    (l: any) => l.lage !== 'da' && l.lage !== 'weg_mit_grund');
  const offen = sorgen.reduce((n: number, l: any) => n + l.anzahl, 0);

  return (
    <div className="karte">
      <Kartenkopf
        titel="Die Ablage nachsehen"
        offen={befund ? offen : 0}
        hart={!!befund && offen > 0}
        stand={befund
          ? `${befund.angesehen} Datei(en) angesehen`
          : stand
            ? `${stand.bezuege ?? 0} Datei(en) hängen an einem Auftrag`
            : 'noch nicht nachgesehen'} />
      <div className="leib">
        <p className="klein leise" style={{ marginTop: 0 }}>
          Ob eine Datei wirklich da ist, steht nicht in der Datenbank, sondern
          auf der Platte. Dieser Knopf sieht dort nach - für <b>Bilder genauso
          wie für PDF</b>. Was fehlt, wird mit seiner Baustelle und dem
          Ordnernamen genannt: die Ablage ist inhaltsadressiert, wer den
          Ordner noch einmal überträgt, bekommt die Datei zurück, und es
          entsteht keine zweite.
        </p>

        <Fehlerkasten lage={fehler} />

        <label className="klein" style={{ display: 'block', marginBottom: 8 }}>
          <input type="checkbox" checked={genau}
                 onChange={(e) => setGenau(e.target.checked)} />
          {' '}Auch die Bytes gegenrechnen
          <span className="leise">
            {' '}- findet die beschädigte Datei, die daliegt und sich öffnen
            lässt. Dauert bei vollem Bestand Minuten.
          </span>
        </label>

        <div className="reihe">
          <button className="knopf" disabled={!!laeuft}
                  onClick={() => lauf(false)}>
            {laeuft === 'sehen' ? 'Sieht nach' : 'Nachsehen'}
          </button>
          {/* ANGEBOTEN WIRD DAS GERADEZIEHEN ERST, WENN ES ETWAS ZU TUN GIBT.
              Ein Knopf, der nichts tut, sieht aus wie ein kaputter. */}
          {befund && !befund.anwenden && offen > 0 && (
            <button className="knopf haupt" disabled={!!laeuft}
                    onClick={() => lauf(true)}>
              {laeuft === 'anwenden' ? 'Zieht gerade' : 'Vermerke gerade ziehen'}
            </button>
          )}
        </div>

        {befund && (
          <>
            <p className="klein" style={{ marginTop: 10 }}>
              <b>{befund.klartext}</b>
              {befund.gekuerzt && (
                <span className="leise">
                  {' '}Angesehen wurden die ersten {befund.hoechstens};
                  es stehen mehr da.
                </span>
              )}
              {befund.anwenden && befund.geradegezogen > 0 && (
                <span className="leise">
                  {' '}{befund.geradegezogen} Vermerk(e) geradegezogen.
                </span>
              )}
            </p>

            {/* JEDE ZAHL MIT IHREM NÄCHSTEN SCHRITT DANEBEN. */}
            <div className="rollt">
              <table className="liste">
                <thead>
                  <tr><th>Lage</th><th>Dateien</th><th>Was zu tun ist</th></tr>
                </thead>
                <tbody>
                  {befund.lagen.map((l: any) => (
                    <tr key={l.lage}>
                      <td>{l.satz}</td>
                      <td className="einfarbig">{l.anzahl}</td>
                      <td className="leise">{l.tun || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* UND DIE ORDNER, DIE NOCH EINMAL HOCHGEHÖREN. Das ist der
                naechste Schritt und nicht die Zahl darueber. */}
            {befund.baustellen.length > 0 && (
              <div className="rollt" style={{ marginTop: 10 }}>
                <table className="liste">
                  <thead>
                    <tr>
                      <th>Diesen Ordner noch einmal übertragen</th>
                      <th>Dateien</th>
                    </tr>
                  </thead>
                  <tbody>
                    {befund.baustellen.map((b: any) => (
                      <tr key={b.auftrag_id}>
                        <td>
                          {b.ordner}
                          <div className="neben">{(b.namen ?? []).join(', ')}</div>
                        </td>
                        <td className="einfarbig">{b.dateien}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* WAS AN KEINEM AUFTRAG HAENGT, LÄSST SICH ÜBER KEINEN ORDNER
                NACHLIEFERN - ein anderer nächster Schritt, also eine eigene
                Zeile und keine stillschweigend fehlende. */}
            {befund.ohne_auftrag > 0 && (
              <p className="klein leise">
                {befund.ohne_auftrag} davon hängen an keinem Auftrag und
                lassen sich über keinen Ordner nachliefern.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * WIE WEIT DIE VORBEREITUNG IST - IM GANZEN BESTAND.
 *
 * ORIGINALTEXT MARVIN (09.09.2026)
 *   „er hat aufträge nicht als «vorbereitet» markiert obwohl sie das schon
 *    sind ... ich will, dass das system überall automaisch scannt und
 *    aktualisiert, pusht und alles auch wenn änderungen einprogrammiert
 *    werden, dass diese für alle aufträge gelten und nicht erst ab den neuen."
 *
 * SEIN ERSTER SATZ WAR ZU PRÜFEN, UND DIE HÄLFTE DAVON STIMMTE NICHT.
 * „Vorbereitet" ist kein gespeicherter Haken: der Chip in der Akte rechnet
 * bei jedem Öffnen neu, und damit gilt jede Änderung an der Regel rückwirkend
 * für den ganzen Bestand. Was gefehlt hat, ist der ÜBERBLICK - es liess sich
 * nicht sagen, wie viele es sind und woran es bei den übrigen hängt.
 *
 * SIE IST EIN WERKZEUG UND KEIN BEFUND, und deshalb steht sie auch dann da,
 * wenn nichts offen ist: wer nachsehen will, sucht den Knopf genau dann,
 * wenn gerade nichts gemeldet wird. Dieselbe Bauart wie „Die Ablage
 * nachsehen".
 */
function VorbereitungNachsehen() {
  const [d, setD] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [grund, setGrund] = useState('');

  async function lauf(nur = '') {
    setLaeuft(true); setFehler(null);
    try { setD(await api.vorbereitungslage(nur)); setGrund(nur); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  const z = d?.zahlen;
  const offen = z ? z.pflicht_fehlt + z.lageplan_fehlt + z.gemeinde_fehlt : 0;

  /* JEDE ZAHL MIT IHREM NÄCHSTEN SCHRITT DANEBEN. Eine Zahl ohne ihn ist ein
     Vorwurf und keine Auskunft. */
  const zeilen = z ? [
    { g: 'gruen', satz: 'Vorbereitet', n: z.gruen,
      tun: 'Nichts. AAZ und VAO können raus.' },
    { g: 'nicht_oeffentlich', satz: 'Keine Anordnung nötig',
      n: z.nicht_oeffentlich,
      tun: 'Bei der Terminvergabe wurde privater Grund oder Sammel-VAO gewählt.' },
    { g: 'pflicht_fehlt', satz: 'Angaben fehlen', n: z.pflicht_fehlt,
      tun: 'In der Akte „Vorbereiten" öffnen und die Felder füllen.' },
    { g: 'lageplan_fehlt', satz: 'Lageplan fehlt', n: z.lageplan_fehlt,
      tun: 'LAP_Maps erzeugen oder den Auftragsordner übertragen.' },
    { g: 'gemeinde_fehlt', satz: 'Gemeinde ohne Empfängeradresse',
      n: z.gemeinde_fehlt,
      tun: 'In den Stammdaten die Behördenmailadresse eintragen.' },
  ] : [];

  return (
    <div className="karte">
      <Kartenkopf
        titel="Die Vorbereitung nachsehen"
        offen={d ? offen : 0}
        hart={!!d && offen > 0}
        stand={z ? `${z.gruen} von ${z.auftraege} vorbereitet`
                 : 'noch nicht nachgesehen'} />
      <div className="leib">
        <p className="klein leise" style={{ marginTop: 0 }}>
          „Vorbereitet" ist <b>kein Haken, sondern eine Rechnung</b> - sie
          läuft bei jedem Öffnen neu und gilt damit rückwirkend für jeden
          Auftrag, auch für die alten. Dieser Knopf rechnet sie für den ganzen
          Bestand auf einmal, damit sich sagen lässt, woran es hängt. Der Lauf
          „Vorbereitung" meldet dieselben Zahlen jeden Morgen.
        </p>

        <Fehlerkasten lage={fehler} />

        <div className="reihe">
          <button className="knopf" disabled={laeuft} onClick={() => lauf('')}>
            {laeuft ? 'Rechnet' : 'Nachsehen'}
          </button>
        </div>

        {z && (
          <>
            <div className="rollt" style={{ marginTop: 10 }}>
              <table className="liste">
                <thead>
                  <tr><th>Stand</th><th>Aufträge</th><th>Was zu tun ist</th></tr>
                </thead>
                <tbody>
                  {zeilen.map((r) => (
                    <tr key={r.g}>
                      <td className="knopfleiste">
                        <button className="knopf klein" disabled={laeuft}
                                onClick={() => lauf(r.g === grund ? '' : r.g)}>
                          {r.satz}
                        </button>
                      </td>
                      <td className="einfarbig">{r.n}</td>
                      <td className="leise">{r.tun}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="klein" style={{ marginTop: 10 }}>
              {z.ohne_umgebungsbilder} Auftrag/Aufträge ohne Umgebungsbilder.
              <span className="leise">
                {' '}Sie entstehen im Lageplan über „Umgebungsbilder holen"
                und liegen im Ordner „Umgebung".
              </span>
            </p>

            {z.felder.length > 0 && (
              <p className="klein leise">
                Am häufigsten fehlt:{' '}
                {z.felder.slice(0, 5)
                  .map((f: any) => `${f.feld} (${f.n})`).join(', ')}.
              </p>
            )}

            {/* WAS NICHT MITZÄHLT, STEHT DABEI. Ein Feld, das still aus der
                Rechnung fällt, ist von einem vergessenen nicht zu
                unterscheiden. */}
            {z.ohne_person.length > 0 && (
              <p className="klein leise">
                Nicht mitgezählt, weil es beim Öffnen gefüllt wird:{' '}
                {z.ohne_person.join(', ')}.
              </p>
            )}

            {grund && (
              <div className="rollt" style={{ marginTop: 10 }}>
                <table className="liste">
                  <thead>
                    <tr><th>Baustelle</th><th>KLS</th><th>SM</th>
                        <th>Woran es hängt</th></tr>
                  </thead>
                  <tbody>
                    {d.lagen.map((l: any) => (
                      <tr key={l.auftragId}>
                        <td>{l.anschrift}
                          <span className="leise"> {l.ort ?? ''}</span></td>
                        <td className="einfarbig">{l.kls_id ?? '-'}</td>
                        <td className="einfarbig">{l.sm_nummer ?? '-'}</td>
                        <td className="leise">
                          {l.fehlt.length ? l.fehlt.join(', ') : l.stand.text}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
